document.addEventListener("DOMContentLoaded", function () {
  class FixedAtc extends HTMLElement {
    constructor() {
      super();
      this.isInCart = false;
      this.init();
    }

    init() {
      this.button = this.querySelector("[data-fixed-atc-button]");
      this.variantId = this.button?.dataset.varId;
      this.sellingPlanId = this.button?.dataset.sellingPlanId;
      this.isProductLink = this.button?.dataset.isProductLink === "true";
      this.productLinkUrl = this.button?.dataset.productLinkUrl;
      this.scrollTargetId = this.button?.dataset.scrollTarget;
      this.priceElement = this.querySelector(
        "[data-fixed-atc-subscription-price]"
      );
      this.originalPrice = Number.parseInt(
        this.priceElement?.dataset.originalPrice,
        10
      );
      this.cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");

      if (this.variantId) {
        this.checkIfInCart();
      }

      this.setupScrollListener();
      this.setupClickListener();

      if (this.priceElement && Number.isFinite(this.originalPrice)) {
        this.setupPriceSync();
      }
    }

    disconnectedCallback() {
      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
      }
      if (this.handleScroll) {
        window.removeEventListener("scroll", this.handleScroll);
      }
      this.classList.remove("active");
    }

    async checkIfInCart() {
      if (!this.variantId) return;

      try {
        const response = await fetch("/cart.js");
        const cartData = await response.json();

        this.isInCart = cartData.items.some(
          (item) => item.variant_id.toString() === this.variantId.toString()
        );

        if (this.isInCart) {
          this.classList.remove("active");
        }
      } catch (error) {
        console.error("Error checking cart:", error);
      }
    }

    setupScrollListener() {
      const section = this.closest(".fixed-atc-section");
      const trigger = section?.querySelector(".fixed-atc__trigger");
      if (!trigger) return;

      const self = this;

      const updateVisibility = () => {
        if (self.isInCart) {
          self.classList.remove("active");
          return;
        }

        const triggerRect = trigger.getBoundingClientRect();

        if (triggerRect.bottom <= window.innerHeight) {
          self.classList.add("active");
        } else if (triggerRect.top > window.innerHeight) {
          self.classList.remove("active");
        }
      };

      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (self.isInCart) {
            self.classList.remove("active");
            return;
          }

          const triggerRect = trigger.getBoundingClientRect();

          if (entry.isIntersecting || triggerRect.bottom < 0) {
            self.classList.add("active");
          } else if (triggerRect.top > window.innerHeight) {
            self.classList.remove("active");
          }
        });
      };

      this._intersectionObserver = new IntersectionObserver(observerCallback, {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      });
      this._intersectionObserver.observe(trigger);

      setTimeout(updateVisibility, 100);

      this.handleScroll = updateVisibility;
      window.addEventListener("scroll", this.handleScroll, { passive: true });
    }

    getStickyHeaderOffset() {
      let offset = 0;

      const announcement = document.querySelector(".announcement-bar-section");
      if (announcement) {
        const style = window.getComputedStyle(announcement);
        if (style.display !== "none" && announcement.offsetHeight > 0) {
          offset += announcement.offsetHeight;
        }
      }

      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height"
        )
      );
      if (Number.isFinite(headerHeight) && headerHeight > 0) {
        offset += headerHeight;
      } else {
        const sectionHeader = document.querySelector(".section-header");
        if (sectionHeader) offset += sectionHeader.offsetHeight;
      }

      return offset;
    }

    setupClickListener() {
      if (!this.button) return;

      this.button.addEventListener("click", (e) => {
        e.preventDefault();

        if (this.scrollTargetId) {
          const target = document.getElementById(this.scrollTargetId);
          if (!target) return;

          const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches;

          if (prefersReducedMotion) {
            const offset = this.getStickyHeaderOffset();
            const targetY =
              target.getBoundingClientRect().top +
              window.pageYOffset -
              offset;
            window.scrollTo(0, targetY);
          } else {
            this.smoothScrollToElement(target, 500);
          }

          if (window.history && window.history.replaceState) {
            window.history.replaceState(
              null,
              "",
              "#" + this.scrollTargetId
            );
          }
          return;
        }

        if (this.isProductLink && this.productLinkUrl) {
          window.location.href = this.productLinkUrl;
          return;
        }

        this.addToCart();
      });
    }

    smoothScrollToElement(element, duration) {
      const startY = window.pageYOffset;
      const offset = this.getStickyHeaderOffset();
      const targetY =
        element.getBoundingClientRect().top + startY - offset;
      const distance = targetY - startY;
      const startTime = performance.now();

      const step = (timestamp) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        window.scrollTo(0, startY + distance * progress);
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    }

    setupPriceSync() {
      document.addEventListener("discount-price-updated", (event) => {
        this.updatePriceFromDiscount(event.detail);
      });
    }

    updatePriceFromDiscount(discountDetail = {}) {
      if (
        discountDetail.active &&
        Number.isFinite(discountDetail.subscriptionPrice)
      ) {
        this.priceElement.innerHTML = "";

        const originalPrice = document.createElement("s");
        originalPrice.textContent = this.formatPrice(this.originalPrice);

        const discountedPrice = document.createElement("span");
        discountedPrice.className = "discounted-price";
        discountedPrice.textContent = this.formatPrice(
          discountDetail.subscriptionPrice
        );

        this.priceElement.appendChild(originalPrice);
        this.priceElement.appendChild(document.createTextNode(" "));
        this.priceElement.appendChild(discountedPrice);
        return;
      }

      this.priceElement.textContent = this.formatPrice(this.originalPrice);
    }

    formatPrice(priceInMinorUnit) {
      const currency = window.getDopa?.currency || "GBP";
      const locale =
        window.getDopa?.locale || document.documentElement.lang || "en-GB";

      try {
        const formatter = new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
        });
        const fractionDigits =
          formatter.resolvedOptions().maximumFractionDigits;
        const divisor = Math.pow(10, fractionDigits);

        return formatter.format(priceInMinorUnit / divisor);
      } catch (error) {
        console.error("Error formatting fixed ATC price:", error);
        return `£${(priceInMinorUnit / 100).toFixed(2)}`;
      }
    }

    addToCart() {
      if (!this.variantId) {
        console.error("No variant ID found");
        return;
      }

      this.button.classList.add("loading");
      this.button.disabled = true;

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData();
      formData.append("id", this.variantId);
      formData.append("quantity", 1);
      if (this.cart) {
        formData.append(
          "sections",
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append("sections_url", window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      if (this.sellingPlanId) {
        formData.append("selling_plan", this.sellingPlanId);
      }

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((data) => {
          this.handleSuccess(data);
        })
        .catch((error) => {
          console.error("Add to cart error:", error);
          this.handleError("An error occurred while adding to cart");
        })
        .finally(() => {
          this.button.classList.remove("loading");
          this.button.disabled = false;
        });
    }

    handleSuccess(data) {
      if (this.cart) this.cart.renderContents(data);
      if (this.cart && this.cart.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }

      this.isInCart = true;
      this.classList.remove("active");
    }

    handleError(message) {
      console.error("Cart error:", message);
      alert(message);
    }
  }

  customElements.define("fixed-atc", FixedAtc);
});
